import React from "react";

export type OrderStatus =
  | "pending"
  | "processing"
  | "shipped"
  | "delivered"
  | "cancelled";

interface StatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  currentStatus?: OrderStatus;
  selectedStatus: string;
  onSelectStatus: (status: OrderStatus | "") => void;
  statusColors: Record<OrderStatus, string>;
  isRTL?: boolean;
  t: (key: string) => string;
  getStatusText: (status: OrderStatus) => string;
}

const ALL_STATUSES: OrderStatus[] = [
  "pending",
  "processing",
  "shipped",
  "delivered",
  "cancelled",
];

const StatusModal: React.FC<StatusModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading,
  currentStatus,
  selectedStatus,
  onSelectStatus,
  statusColors,
  isRTL = false,
  t,
  getStatusText,
}) => {
  if (!isOpen) return null;

  const statusOptions = ALL_STATUSES.filter(
    (status) => status !== currentStatus
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl ${
          isRTL ? "text-right" : ""
        }`}
      >
        <div
          className={`flex items-center justify-between mb-6 ${
            isRTL ? "flex-row-reverse" : ""
          }`}
        >
          <h3 className="text-xl font-bold text-gray-800">
            {t("order.updateOrderStatus")}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            aria-label={t("common.close")}
          >
            ✕
          </button>
        </div>

        <div className="mb-6">
          <label
            className={`block text-sm font-medium text-gray-700 mb-2 ${
              isRTL ? "text-right" : ""
            }`}
          >
            {t("order.currentStatus")}
          </label>
          {currentStatus && (
            <div
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                statusColors[currentStatus]
              }`}
            >
              {getStatusText(currentStatus)}
            </div>
          )}
        </div>

        <div className="mb-6">
          <label
            className={`block text-sm font-medium text-gray-700 mb-2 ${
              isRTL ? "text-right" : ""
            }`}
          >
            {t("order.selectNewStatus")}
          </label>
          <select
            value={selectedStatus}
            onChange={(e) => onSelectStatus(e.target.value as OrderStatus)}
            className={`w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-200 ${
              isRTL ? "text-right" : ""
            }`}
          >
            <option value="">{t("order.selectStatus")}</option>
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {getStatusText(status)}
              </option>
            ))}
          </select>
        </div>

        <div
          className={`flex justify-end gap-3 ${
            isRTL ? "flex-row-reverse" : ""
          }`}
        >
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors duration-200 font-medium"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading || !selectedStatus}
            className="px-5 py-2.5 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                {t("common.updating")}
              </>
            ) : (
              t("order.updateStatus")
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusModal;
